import { useLocalObservable } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';

import { Call } from '@gem/call/CallStore/Call';
import { ICallAvatar } from '@gem/call/CallStore/Call.types';
import { IEventItem } from '@gem/call/CallStore/Events/Events.types';
import { Member } from '@gem/call/CallStore/Member/Member';
import { useCallContext } from '@gem/call/CallView/CallView.context';
import { ElectronApi } from '@gem/call/Removable/packages/electron/ElectronApi';
import { CallEventTypes } from '@gem/call/constants';
import { useTranslation } from '@gem/modules/i18n';
import dateService from '@gem/services/date-service';

type IEventWithUser = IEventItem & {
  user: Member | null;
};

interface INotification {
  id: number;
  title: string;
  icon: string;
  body: string;
  onClick(e: any): void;
}

interface ICallPushNotificationPresenter {
  lastEvent: number;
  call: Call;
  avatar: ICallAvatar | null;
  events: IEventWithUser[];
  notifications: INotification[];
  onShowNotification(event: number): void;
  onClick(event: any): void;
}

export const useCallPushNotificationPresenter = (
  redirectRoutePath?: string
): ICallPushNotificationPresenter => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const context = useCallContext();

  const presenter = useLocalObservable<ICallPushNotificationPresenter>(() => ({
    lastEvent: +dateService.now(),

    get call(): Call {
      return context.call;
    },

    get avatar(): ICallAvatar | null {
      return presenter.call.avatar || null;
    },

    get events(): IEventWithUser[] {
      return presenter.call.events.list
        .filter((event) => {
          return event.type === CallEventTypes.RaiseHand;
        })
        .filter((event) => {
          const isActualHandRaisedEvent = event.timestamp > presenter.lastEvent;

          if (!isActualHandRaisedEvent || ElectronApi.isEnabled) {
            return false;
          }

          return presenter.call.members.getMemberById(event.userId.toString());
        })
        .map((data) => {
          const user = presenter.call.members.getMemberById(data.userId.toString());
          return { ...data, user };
        });
    },

    get notifications(): INotification[] {
      return presenter.events.map((event) => {
        const body = t('calls_raise_hand_event_description').replace(
          '{0}',
          event.user?.displayName || ''
        );

        return {
          id: event.timestamp,
          title: presenter.avatar?.title || '',
          icon: presenter.avatar?.url || '',
          body,
          onClick: presenter.onClick,
        };
      });
    },

    onShowNotification(timestamp: number): void {
      presenter.lastEvent = timestamp;
    },

    onClick({ target }: any): void {
      window.focus();
      target.close();

      if (redirectRoutePath) {
        navigate(redirectRoutePath);
      }
    },
  }));

  return presenter;
};
